import React from 'react';
import { useTranslation } from 'next-i18next';

import { CodeVerificationInput, ModalBody } from '@components/ui';
import * as Notifications from '@common/Notification';
import { ERROR_CODES } from '@constants/errors';
import { addRoPrefix } from '@helpers/PhoneNumberHelpers';
import { useAccountMutation } from '@hooks/data/useAccountMutation';
import { useConfig } from '@hooks/data/useConfig';

import PhoneVerificationHelp from './PhoneVerificationHelp';

import styles from './PhoneVerificationModalVerificationStep.module.scss';

interface Props {
  onBack: () => void,
  onClose: () => void,
  onSubmitSuccess?: () => void,
  phoneNumber: string,
  startTimer: () => void,
  isTimerRunning: boolean,
  timerTotalSeconds: number
}

const PhoneVerificationModalVerificationStep = (props: Props) => {

  const {
    phoneNumber = '',
    isTimerRunning,
    timerTotalSeconds,
    startTimer,
    onClose,
    onBack,
    onSubmitSuccess
  } = props;

  const [code, setCode] = React.useState<string>();
  const [error, setError] = React.useState<string>();

  const { validatePhoneVerificationCode, sendPhoneVerificationCode, isLoading: isMutationLoading } = useAccountMutation();
  const { t } = useTranslation();

  const { data: config } = useConfig();
  const { verifyPhoneNumber } = config || {};
  const {
    header: headerCopy = {
      smsCode: t('PHONE_VALIDATION.VERIFICATION_STEP.TITLE')
    },
    button: buttonCopy = {
      confirm: t('PHONE_VALIDATION.VERIFICATION_STEP.PRIMARY_BUTTON')
    },
    body: bodyCopy = {
      smsCode: t('PHONE_VALIDATION.VERIFICATION_STEP.SUBTITLE')
    },
    postAction: postActionCopy = {
      resendCode: t('PHONE_VALIDATION.VERIFICATION_STEP.SECONDARY_BUTTON')
    }
  } = verifyPhoneNumber || {};

  React.useEffect(() => {
    if (code?.length === 4) {
      handleSubmit();
    }
  }, [code]);

  const handleSubmit = () => {
    validatePhoneVerificationCode({
      // Automatically add hardcoded prefix for now
      data: { code, phoneNumber: addRoPrefix(phoneNumber) },
      onError: (err) => {
        if (err?.response?.status === ERROR_CODES.VALIDATION_ERROR) {
          const { violations } = err?.response?.data || {};
          const violationMessage = violations?.[0]?.message;
          setError(violationMessage || t('ERRORS.DEFAULT_TOAST'));
        } else {
          Notifications.showError(t('ERRORS.DEFAULT_TOAST'), { autoClose: 2000, toastId: 'phoneCodeVerificationError' });
        }
      },
      onSuccess: () => {
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      }
    });
  };

  const handleComplete = (value: string) => {
    setCode(value);
  };

  const handleChange = (value: string) => {
    if (error) {
      setError(undefined);
    }
    setCode(value);
  };

  const handleReset = () => {
    setCode('');
    setError(undefined);
  };

  const handleResend = () => {
    handleReset();
    sendPhoneVerificationCode({
      data: { phoneNumber: addRoPrefix(phoneNumber) },
      onSuccess: () => {
        startTimer();
      },
      onError: () => {
        Notifications.showError(t('ERRORS.DEFAULT_TOAST'), { autoClose: 2000, toastId: 'phoneCodeVerificationError' });
      }
    });
  };

  const secondaryButtonText = isTimerRunning ? `${postActionCopy.resendCode} în ${timerTotalSeconds}` : postActionCopy.resendCode;
  const subtitleText = bodyCopy.smsCode?.replace('%phone%', addRoPrefix(phoneNumber));

  return (
    <ModalBody
      className={styles.root}
      childrenClassName={styles.content}
      headerProps={{
        className: styles.header,
        title: headerCopy?.smsCode,
        withBackButton: true,
        onClose,
        onBack,
        desktopProps: {
          size: 'large',
          withCloseButton: true
        },
        responsiveProps: {
          size: 'medium',
          withCloseButton: true
        },
      }}
      actionsProps={{
        withBorder: false,
        withPadding: false,
        className: styles.actions,
        innerClassName: styles.actionsInner,
        primary: {
          text: buttonCopy.confirm,
          disabled: !code || isMutationLoading || (code?.length !== 4),
          loading: isMutationLoading,
          onClick: handleSubmit
        },
        secondary: {
          text: secondaryButtonText,
          className: styles.resendBtn,
          variant: 'empty',
          disabled: isMutationLoading || isTimerRunning,
          onClick: handleResend
        }
      }}
    >
      <div className={styles.subtitle}>{subtitleText}</div>
      <div className={styles.codeInputWrapper}>
        <CodeVerificationInput
          error={error}
          errorClassName={styles.error}
          disabled={isMutationLoading}
          className={styles.codeInput}
          onChange={handleChange}
          onComplete={handleComplete}
          inputProps={{
            onClick: () => {
              if (error) {
                handleReset();
              }
            }
          }}
          value={code}
        />
      </div>
      <PhoneVerificationHelp className={styles.help} />
    </ModalBody>
  );
};

export default PhoneVerificationModalVerificationStep;
